/**
 * Reine Logik des Wizards (kein React) — testbar ohne Rendern.
 *
 * Die Pfad-/Ablauflogik der Nachrichten liegt im Backend (`/flow/plan`); hier steht nur, was die
 * Oberfläche selbst entscheidet: Schritt-Gates, Zusammenfassungen, Antworten für den Ablauf.
 */

import {
  Audience,
  CriteriaSet,
  JudgementClass,
  PrimaryLanguage,
  Radius,
  RADIUS_STEPS,
  RecruitsFor,
  BriefingDerivation,
  WizardAccount,
  WizardFlowAnswers,
  WizardState,
  WIZARD_DEFAULTS,
  WizardStep,
} from './campaign-wizard.types';
import { adSignatureSource, hashAdText } from './steps/job/job-gate.logic';

export const fmtNumber = (n: number): string => n.toLocaleString('de-DE');

/** Klassen, die der Nutzer zum Ansprechen wählen kann. C (Ausschlusskriterium trifft zu) wird nie angesprochen. */
export const CLASS_KEYS: JudgementClass[] = ['A', 'B'];

/** „A + B“ bzw. „keine“. */
export const classesText = (classes: Record<JudgementClass, boolean>): string =>
  CLASS_KEYS.filter(key => classes[key]).join(' + ') || 'keine';

export const criteriaEqual = (a: CriteriaSet, b: CriteriaSet): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** Normalisierter Vergleich für Blacklist-Namen (Groß-/Kleinschreibung egal). */
export const sameName = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/** Nur der Vanity-Name einer LinkedIn-Profil-URL, sonst der Text selbst. */
export const profileLabel = (url: string): string =>
  url.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, '').replace(/\/$/, '');

/** Standard-Radius eines neuen Ortes: 40 km (= 25 Meilen). */
export const DEFAULT_RADIUS: Radius = 40;

/**
 * Erste LinkedIn-Stufe ≥ Wert (alte Entwürfe: 10 → 15, 25 → 40, 50 → 55, 100 → 120, > 160 → 160): eine Suche
 * wird nie enger als gemeint. Muss identisch zum Backend sein (`wizard-radius.ts`).
 */
export const normalizeRadius = (value: number): Radius => {
  if (!Number.isFinite(value) || value <= 0) return DEFAULT_RADIUS;
  return RADIUS_STEPS.find(step => step >= value) ?? RADIUS_STEPS[RADIUS_STEPS.length - 1];
};

/** Radius eines gespeicherten Ortes: `null` = ganze Region, alles andere wie `normalizeRadius`. Identisch zum Backend. */
export const normalizeLocationRadius = (value: unknown): Radius | null =>
  value === null ? null : normalizeRadius(Number(value));

export const nextRadius = (radius: number): Radius => {
  const i = RADIUS_STEPS.indexOf(normalizeRadius(radius));
  return RADIUS_STEPS[(i + 1) % RADIUS_STEPS.length];
};

export const accountDisplayName = (
  account: { firstName?: string; lastName?: string; userEmail?: string } | null | undefined
): string => {
  if (!account) return '';
  const name = `${account.firstName ?? ''} ${account.lastName ?? ''}`.trim();
  return name || account.userEmail || '';
};

/** Zeile „Das weiß ich schon: …“ (Schritt 5) und Kurzform in Schritt 6. */
export const accountKnownText = (productType: WizardAccount['productType'] | undefined): string =>
  productType === 'recruiter'
    ? 'Konto: Recruiter, InMail-Guthaben'
    : productType === 'sales_navigator'
      ? 'Konto: Sales Navigator, InMail-Guthaben'
      : 'Konto: LinkedIn ohne erkennbares Premium';

/** Recruiter und Sales Navigator haben InMail-Guthaben: Schritt 5 fragt, ob eine nicht angenommene Anfrage per InMail nachgefasst wird. */
export const asksInMailFallback = (
  s: Pick<WizardState, 'audience'>,
  productType: WizardAccount['productType'] | undefined
): boolean =>
  s.audience !== 'existing' && (productType === 'recruiter' || productType === 'sales_navigator');

/** Die Frage nach InMail-Guthaben stellt nur ein normales Konto und nur bei neuen Kontakten. */
export const needsInMailCredit = (
  state: Pick<WizardState, 'audience'>,
  productType: WizardAccount['productType'] | undefined
): boolean => state.audience !== 'existing' && (productType ?? 'normal') === 'normal';

export const audienceText: Record<Audience, string> = {
  new: 'Nur neue Kontakte',
  existing: 'Nur bestehende Kontakte',
  both: 'Neue + bestehende',
};

const languageText = (language: PrimaryLanguage): string =>
  language === 'de' ? 'Deutsch' : language === 'en' ? 'Englisch' : 'Sprache automatisch';

/** Zusammenfassung der Ansprache (Schritt 7, Zeile „Ansprache“). */
export const ansprachSummary = (s: WizardState): string =>
  (s.recruitsFor === 'own' ? 'Eigenes Unternehmen' : 'Als Dienstleister') +
  ' · ' +
  audienceText[s.audience] +
  (s.audience !== 'existing'
    ? s.withNote
      ? ' · Anfrage mit Nachricht'
      : ' · Anfrage ohne Nachricht'
    : '') +
  (s.referral ? ' · Empfehlung ' + (s.bonus || '') : '') +
  ' · ' +
  languageText(s.language);

/** Antworten für `/flow/plan` und `/flow/test`. */
export const flowAnswersFromState = (
  s: WizardState,
  account: Pick<WizardAccount, 'productType'> | null | undefined
): WizardFlowAnswers => {
  const answers: WizardFlowAnswers = {
    audience: s.audience,
    formalSpeech: s.formalSpeech === true,
    language: s.language,
    secondaryLanguage: s.language2 === '' ? 'none' : s.language2,
    recruitsFor: s.recruitsFor as RecruitsFor,
    withNote: s.withNote,
    chatCount: s.chatCount,
    referral: s.referral ? { enabled: true, bonus: s.bonus.trim() } : { enabled: false },
    emojis: s.emojis,
    swissS: s.swissS,
    calendar: s.calendar ? { enabled: true, url: s.calendarUrl.trim() } : { enabled: false },
  };
  if (s.waitMinutes && Object.keys(s.waitMinutes).length > 0) answers.waitMinutes = s.waitMinutes;
  // Pflichtfrage wie in der alten Anlage: ohne Antwort gibt es kein hasInMailCredit (der Plan rechnet dann ohne InMails).
  if (needsInMailCredit(s, account?.productType) && s.inmailCredit !== null)
    answers.hasInMailCredit = s.inmailCredit;
  if (asksInMailFallback(s, account?.productType))
    answers.inmailFallback = s.inmailFallback !== false;
  return answers;
};

export interface GateContext {
  hasAccount: boolean;
  /** Der Nutzer hat in Schritt 2 mindestens einen Ort oder (bei eigener Suche) eine gültige URL. */
  searchReady?: boolean;
  /** Kontoart des Absenders: nur ein normales Konto muss die Frage nach dem InMail-Guthaben beantworten. */
  productType?: WizardAccount['productType'];
  /** Am Absender-Konto fehlen Arbeitstage oder -zeiten (Schritt 7: Start würde verweigert). */
  workWindowMissing?: boolean;
}

/**
 * Fehlen am Konto Arbeitstage oder -zeiten? Nur wenn das Konto die Angaben überhaupt mitliefert (undefined = unbekannt,
 * dann entscheidet der Server beim Start). Ohne Fenster würden alle Schritte endlos verschoben.
 */
export const accountWorkWindowMissing = (
  account: Pick<WizardAccount, 'workDays' | 'workHours'> | null | undefined
): boolean =>
  !!account &&
  (account.workDays !== undefined || account.workHours !== undefined) &&
  ((account.workDays ?? []).length === 0 || (account.workHours ?? []).length === 0);

const LINKEDIN_URL = /^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/.+/i;
export const isLinkedinUrl = (value: string): boolean => LINKEDIN_URL.test(value.trim());

/** KI-Bewertung übersprungen? Nur bei eigenen Suchlinks möglich (dort hat der Nutzer schon selbst ausgewählt). */
export const judgingSkipped = (s: Pick<WizardState, 'method' | 'skipJudging'>): boolean =>
  s.method === 'url' && s.skipJudging === true;

/** Die Zeilen der eigenen Links in Schritt 2 (mindestens eine, auch leer): `searchUrls`, sonst der eine `searchUrl`. */
export const searchLinkRows = (s: Pick<WizardState, 'searchUrl' | 'searchUrls'>): string[] =>
  (s.searchUrls ?? []).length > 0 ? s.searchUrls : [s.searchUrl ?? ''];

/** Zeilen setzen: `searchUrl` bleibt der erste Link (Altbestand, Übersicht). */
export const withSearchLinks = (rows: string[]): Pick<WizardState, 'searchUrl' | 'searchUrls'> => ({
  searchUrls: rows,
  searchUrl: rows.find(r => r.trim() !== '')?.trim() ?? '',
});

/** Darf der Nutzer von diesem Schritt weiter? Der Text sagt, was fehlt. */
export const stepBlocker = (step: WizardStep, s: WizardState, ctx: GateContext): string | null => {
  switch (step) {
    case 1:
      if (s.briefing.trim().length === 0 || /^https?:\/\/\S+$/i.test(s.briefing.trim()))
        return 'briefing';
      if (s.name.trim().length === 0) return 'name';
      if (!ctx.hasAccount || !s.accountId) return 'account';
      return null;
    case 2:
      if (s.method === 'url') return searchLinkRows(s).some(isLinkedinUrl) ? null : 'searchUrl';
      return s.locations.length > 0 ? null : 'locations';
    case 3:
      if (judgingSkipped(s)) return null;
      return CLASS_KEYS.some(k => s.classes[k]) ? null : 'classes';
    case 5:
      if (s.referral && s.bonus.trim().length === 0) return 'bonus';
      if (s.calendar && s.calendarUrl.trim().length === 0) return 'calendarUrl';
      // Pflichtfrage: kein stilles „Nein“ (wie in der alten Anlage).
      return needsInMailCredit(s, ctx.productType) && s.inmailCredit === null
        ? 'inmailCredit'
        : null;
    case 7:
      if (ctx.workWindowMissing) return 'workWindow';
      // Pflichtauswahl: Freigabe und Umgang mit Bereits Kontaktierten müssen aktiv gewählt werden (false und fehlend zählen gleich).
      if (s.approveChosen !== true) return 'approveChoice';
      return s.recontactChosen !== true ? 'recontactChoice' : null;
    default:
      return null;
  }
};

/** Stepper: ✓ für alles vor dem höchsten erreichten Schritt (`maxStep`), außer dem angezeigten Schritt. */
export const isStepDone = (n: number, step: number, maxStep?: number): boolean =>
  n !== step && n < Math.max(step, maxStep ?? step);

/**
 * D-12: Nach einem Klon ohne Briefing leitet Schritt 2 nichts ab. Beim Vorrücken aus Schritt 1 füllt die Ableitung nur,
 * was der Nutzer nicht selbst gesetzt hat: Orte (leer), Kriterien (alle leer), Name (leer).
 */
export const needsDerivation = (s: WizardState): boolean =>
  s.briefing.trim().length > 0 &&
  (s.locations.length === 0 ||
    s.criteria.jobTitles.length + s.criteria.experience.length + s.criteria.languages.length === 0);

/** Signatur des Textes, aus dem die Suchkriterien abgeleitet werden (Anzeige + Ergänzungen). */
export const derivationKey = (s: Pick<WizardState, 'briefing' | 'briefingExtras'>): string =>
  hashAdText(adSignatureSource({ briefing: s.briefing, briefingExtras: s.briefingExtras }));

/** Hat sich die Anzeige seit der letzten Ableitung geändert? (Schritt 2 bietet dann die Neuableitung an.) */
export const derivationStale = (s: WizardState): boolean =>
  s.briefing.trim().length > 0 && s.derivedKey !== '' && s.derivedKey !== derivationKey(s);

/**
 * „Suchkriterien neu ableiten“ (nur nach Klick): Jobtitel, Orte-Vorschläge (nur wenn die Anzeige einen Ort nennt),
 * Suchbegriffe und Branchen (nur wenn die Ableitung welche liefert). Name und Rest bleiben unangetastet.
 */
export const applyRederivation = (
  s: WizardState,
  derived: Pick<BriefingDerivation, 'jobTitle' | 'locations' | 'criteria'>
): WizardState => {
  const c = derived.criteria;
  return {
    ...s,
    jobTitle: derived.jobTitle?.trim() ? derived.jobTitle : s.jobTitle,
    locations: derived.locations.length > 0 ? derived.locations : s.locations,
    criteria: {
      ...s.criteria,
      jobTitles: c.jobTitles.length > 0 ? c.jobTitles : s.criteria.jobTitles,
      experience: c.experience.length > 0 ? c.experience : s.criteria.experience,
      languages: c.languages.length > 0 ? c.languages : s.criteria.languages,
      industries: (c.industries ?? []).length > 0 ? c.industries : s.criteria.industries,
    },
    derivedKey: derivationKey(s),
  };
};

export const applyDerivation = (
  s: WizardState,
  derived: {
    name: string;
    jobTitle?: string;
    locations: WizardState['locations'];
    criteria: WizardState['criteria'];
  }
): WizardState => {
  const noCriteria =
    s.criteria.jobTitles.length + s.criteria.experience.length + s.criteria.languages.length === 0;
  return {
    ...s,
    locations: s.locations.length === 0 ? derived.locations : s.locations,
    criteria: noCriteria ? derived.criteria : s.criteria,
    name: s.name.trim() === '' ? derived.name : s.name,
    jobTitle: s.jobTitle.trim() === '' ? (derived.jobTitle ?? '') : s.jobTitle,
    derivedKey: s.derivedKey || derivationKey(s),
  };
};

export const clampStep = (n: number): WizardStep =>
  Math.min(7, Math.max(1, Math.round(n) || 1)) as WizardStep;

/**
 * Ein vom Server geladener Zustand (alter Entwurf, Fallback bei verletztem Schema) wird tief mit den Standardwerten
 * gemischt: neue Felder (`briefingExtras`, `criteria.industries` …) fehlen dann nie, und
 * alte Radiuswerte landen auf den LinkedIn-Stufen.
 */
export const withWizardDefaults = (
  loaded: Partial<WizardState> & { accountId?: string }
): WizardState => {
  const merged = {
    ...WIZARD_DEFAULTS,
    accountId: '',
    ...loaded,
    criteria: { ...WIZARD_DEFAULTS.criteria, ...(loaded.criteria ?? {}) },
    judging: { ...WIZARD_DEFAULTS.judging, ...(loaded.judging ?? {}) },
  } as WizardState;
  // Ältere Entwürfe kennen die Ableitungs-Signatur nicht: der geladene Stand gilt als abgeleitet (sonst fragt Schritt 2 sofort nach).
  if (!merged.derivedKey && merged.briefing.trim()) merged.derivedKey = derivationKey(merged);
  // Entfernte Felder alter Entwürfe (Gehalt, Homeoffice) nicht weiterreichen.
  delete (merged as unknown as Record<string, unknown>).salary;
  delete (merged as unknown as Record<string, unknown>).homeOffice;
  return {
    ...merged,
    locations: (Array.isArray(merged.locations) ? merged.locations : []).map(l => ({
      ...l,
      radius: normalizeLocationRadius(l.radius),
    })),
  };
};
